/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * AX1 CANONICAL BUTTON COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * This is THE single Button component for the entire AX1 application.
 * All button imports should resolve to this file.
 * 
 * Design alignment:
 * - Uses BUTTON_TOKENS from layoutTokens.ts
 * - Signature lower-left corner on primary text actions; ordinary icon controls
 * - semantic brand accent (light/dark and white-label aware)
 * - Strict size scale: sm (h-8), md (h-9), lg (h-11)
 * 
 * Variants:
 * - primary: Brand-blue CTA
 * - default: Alias for primary (backward compat)
 * - secondary: Neutral surfaced action
 * - outline: Border-only
 * - ghost: Transparent
 * - destructive: Red for dangerous actions
 * - success: Soft positive action (use sparingly; completed state belongs in status UI)
 * - accent: Backward-compatible alias for primary
 * - link: Text-only with underline on hover
 */
import React, { forwardRef } from "react";
import { BUTTON_TOKENS, type ButtonVariant, type ButtonSize } from "@/lib/layout/layoutTokens";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  children?: React.ReactNode;
  /** @deprecated asChild is not supported in canonical Button. Migrate to direct child composition. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  startIcon,
  endIcon,
  className,
  children,
  disabled,
  type,
  asChild: _asChild, // Ignored for backward compat (not implemented)
  ...props
}, ref) => {
  const spinner =
    size === "lg" ? "h-5 w-5" : size === "sm" ? "h-4 w-4" : "h-4 w-4";

  return (
    <button
      ref={ref}
      type={type}
      aria-busy={loading || undefined}
      data-ax-variant={variant}
      data-ax-size={size}
      className={cn(
        BUTTON_TOKENS.base,
        BUTTON_TOKENS.variants[variant],
        BUTTON_TOKENS.sizes[size],
        fullWidth && "w-full",
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <svg
          className={cn("animate-spin", spinner)}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-90"
            fill="currentColor"
            d="M4 12a8 8 0 0 1 8-8v4A4 4 0 0 0 8 12H4z"
          />
        </svg>
      )}
      {!loading && startIcon}
      {children && <span className={cn("inline-flex min-w-0 items-center justify-center gap-2", size.startsWith("icon") && "sr-only")}>{children}</span>}
      {!loading && endIcon}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
